/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { ThemeType } from "@packages/ui";
import * as RadixUIDialog from "@radix-ui/react-dialog";
import { AnimatePresence, motion } from "motion/react";
import type { ReactNode } from "react";

import { Durations } from "../../lib/enums/durations";
import { ZIndices } from "../../lib/enums/zIndices";
import { Overlay } from "./Overlay";

export type FullscreenModalProps = {
  children: ReactNode;
  contentCss?: Interpolation<ThemeType>;
  isOpen: boolean;
  onOpenAutoFocus?: (event: Event) => void;
  onOpenChange: (open: boolean) => void;
};

const cssOverlay: Interpolation = {
  zIndex: ZIndices.OverlayFullscreen,
};

const cssMotionDiv: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  backgroundColor: theme.semanticColors.background,
  color: theme.semanticColors.primary,
  display: "flex",
  flexDirection: "column",
  height: "100dvh",
  left: 0,
  overflow: "hidden",
  paddingBottom: theme.spacing.ms,
  paddingLeft: theme.spacing.l,
  paddingRight: theme.spacing.l,
  paddingTop: theme.spacing.ms,
  position: "fixed",
  top: 0,
  width: "100dvw",
  zIndex: ZIndices.ContentFullscreen,
});

export const FullscreenModal = ({
  children,
  contentCss,
  isOpen,
  onOpenAutoFocus,
  onOpenChange,
}: FullscreenModalProps) => (
  <RadixUIDialog.Root onOpenChange={onOpenChange} open={isOpen}>
    <RadixUIDialog.Portal>
      <RadixUIDialog.Overlay asChild>
        <Overlay css={cssOverlay} onClick={() => onOpenChange(false)} />
      </RadixUIDialog.Overlay>
      <AnimatePresence>
        {isOpen && (
          <RadixUIDialog.Content asChild onOpenAutoFocus={onOpenAutoFocus}>
            <motion.div
              animate={{ opacity: 1, scale: 1 }}
              css={[cssMotionDiv, contentCss]}
              exit={{ opacity: 0, scale: 0.95 }}
              initial={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: Durations.Long, type: "spring" }}
            >
              {children}
            </motion.div>
          </RadixUIDialog.Content>
        )}
      </AnimatePresence>
    </RadixUIDialog.Portal>
  </RadixUIDialog.Root>
);
